import React, {Fragment, useState} from 'react';
import {Typography} from '@mui/material';

import {AlertInfo} from 'presentation/components';
import {GlobalExternalLinkEnum, ProductEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {PrepareModal} from 'presentation/modules/myAppointments/ui/components/PrepareModal';
import {translations} from 'presentation/translations-new';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {CardAlertInfoProps} from './types';
import {BookingTypeEnum} from '../../../constants/bookingTypeEnum';
import {SurgeryPrepareOrientations} from 'presentation/modules/myAppointments/ui/components/SurgeryPrepareOrientations';

const {appointmentCard} = translations['pt-br'].privateFlow.myAppointments;
const {bringDocument} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails.appointmentInstructions;

export function AppointmenInstructionsAlertInfo({
	bookingType,
	specialtyCode,
	specialtyName,
	showEnterRoom,
	hideAction,
	alternativeText,
	...alertProps
}: CardAlertInfoProps) {
	const [openPrepareModalState, setOpenPrepareModalState] = useState<boolean>(false);
	const [openSurgeryPrepareModal, setOpenSurgeryPrepareModal] = useState(false);
	const {
		dataHostNameState: {product},
	} = useHostName();
	const isExam = bookingType === BookingTypeEnum.EXAM;
	const isSurgery = bookingType === BookingTypeEnum.SURGERY;

	const handleOpenSurgeryPrepareModal = () => {
		return setOpenSurgeryPrepareModal(true);
	};

	const telehealthOrientations = showEnterRoom ? (
		<Typography variant="body2">{appointmentCard.teleHealth.availableRoom}</Typography>
	) : (
		<Fragment>
			<Typography variant="body2">{appointmentCard.teleHealth.description}</Typography>
			<br />
			<Typography variant="body2">{appointmentCard.teleHealth.watchTutorial}</Typography>
			{hideAction ? null : (
				<AlertInfo.Action
					$iconName="video"
					onClick={handleWatchTutorial}
					data-tag-id={AppointmentBookingEnum.CLICK_ASSISTIR_VIDEO_TELEMEDICINA}>
					{appointmentCard.teleHealth.watchVideo}
				</AlertInfo.Action>
			)}
		</Fragment>
	);

	const examOrientations = (
		<Fragment>
			<Typography variant="body2">
				{alternativeText ? bringDocument : appointmentCard.examOrientations}
			</Typography>
			{hideAction || alternativeText ? null : (
				<AlertInfo.Action onClick={handleOpenInfoAlert} $iconName="appointments-alert">
					{appointmentCard.readOrientations}
				</AlertInfo.Action>
			)}
		</Fragment>
	);

	const surgeryOrientations = (
		<Fragment>
			<Typography variant="body2">{appointmentCard.surgeryOrientations}</Typography>
			{hideAction ? null : (
				<AlertInfo.Action
					onClick={handleOpenSurgeryPrepareModal}
					$iconName="appointments-alert">
					{appointmentCard.readOrientations}
				</AlertInfo.Action>
			)}
		</Fragment>
	);

	const description: Record<BookingTypeEnum, React.ReactNode | string> = {
		[BookingTypeEnum.TELEHEALTH]: telehealthOrientations,
		[BookingTypeEnum.EXAM]: examOrientations,
		[BookingTypeEnum.HEALTH]: bringDocument,
		[BookingTypeEnum.ODONTO]: bringDocument,
		[BookingTypeEnum.SURGERY]: surgeryOrientations,
	};

	const telehealthTutorial = {
		[ProductEnum.HAP]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_HAP,
		[ProductEnum.CCG]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_CCG,
		[ProductEnum.CLINIPAM]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_CLINIPAM,
		[ProductEnum.GNDI_MINAS]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_GNDI_MINAS,
		[ProductEnum.GNDI]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_GNDI_SP,
		[ProductEnum.NOTREDAME_SAUDE]: GlobalExternalLinkEnum.TELEHEALTH_TUTORIAL_GNDI_SP,
	};

	function handleOpenInfoAlert() {
		setOpenPrepareModalState(true);
	}

	function handleWatchTutorial() {
		window.open(telehealthTutorial[product]);
	}

	return (
		<Fragment>
			<AlertInfo.Root {...alertProps}>
				<AlertInfo.Description>{description[bookingType]}</AlertInfo.Description>
			</AlertInfo.Root>
			{isExam && openPrepareModalState ? (
				<PrepareModal
					examNumber={String(specialtyCode)}
					setOpen={setOpenPrepareModalState}
					examName={specialtyName}
					open={openPrepareModalState}
				/>
			) : null}
			{isSurgery && openSurgeryPrepareModal ? (
				<SurgeryPrepareOrientations
					open={openSurgeryPrepareModal}
					setOpen={setOpenSurgeryPrepareModal}
				/>
			) : null}
		</Fragment>
	);
}
